import axios from "axios";
import { WEB_BACKEND } from "./backends";

export const API = `${WEB_BACKEND}/api`;

// ── Caché «stale-while-revalidate» en sessionStorage ─────────────────────────
//
// El backend vive en el plan gratuito de Render, que duerme el servicio a los
// quince minutos de inactividad. Sin caché, cada navegación dentro de la web
// podía toparse de nuevo con el arranque en frío.
//
// La estrategia: pintar al instante lo último que se recibió y revalidar de
// fondo. Se usa `sessionStorage` y no `localStorage` a propósito —el contenido
// es editorial y cambia a diario, así que la copia no debe sobrevivir al cierre
// de la pestaña.
const PREFIJO = "edh:v1:";
const MAX_EDAD_MS = 30 * 60 * 1000; // 30 min

export function leerCache(clave) {
  try {
    const bruto = sessionStorage.getItem(PREFIJO + clave);
    if (!bruto) return null;
    const { ts, data } = JSON.parse(bruto);
    if (!ts || Date.now() - ts > MAX_EDAD_MS) return null;
    return data;
  } catch {
    // sessionStorage puede fallar en modo privado o estar lleno: no es motivo
    // para tumbar la página.
    return null;
  }
}

export function escribirCache(clave, data) {
  try {
    sessionStorage.setItem(PREFIJO + clave, JSON.stringify({ ts: Date.now(), data }));
  } catch {
    /* cuota agotada o almacenamiento bloqueado: se ignora */
  }
}

/**
 * GET contra la API, rechazando respuestas que no sean JSON.
 *
 * La API siempre devuelve objetos o arrays. Si llega una cadena, lo que ha
 * respondido no es la API: puede ser la página de mantenimiento de Render, un
 * portal cautivo, un error de un proxy intermedio o —si falta
 * REACT_APP_BACKEND_URL— el propio index.html servido por el fallback de la
 * SPA, que devuelve HTML con un 200.
 *
 * Sin esta comprobación, ese HTML llegaba a las páginas como si fueran datos
 * buenos y reventaba en el primer `.map` o `.filter`, tirando la página entera
 * contra el límite de error. Tratarlo como un fallo de red hace que se muestre
 * el estado vacío, que es lo correcto.
 */
export async function apiGet(ruta, config) {
  const { data } = await axios.get(`${API}${ruta}`, config);
  if (typeof data === "string") {
    throw new Error(`Respuesta no válida en ${ruta}: se esperaba JSON`);
  }
  return data;
}

/**
 * Carga todo lo que necesita la portada.
 *
 * Intenta primero el endpoint agregado. Si el backend todavía no lo tiene
 * —frontend y backend se despliegan por separado, en Vercel y en Render, y uno
 * puede ir por delante del otro—, recurre a las seis llamadas de siempre en
 * lugar de dejar la portada vacía.
 */
export async function cargarPortada() {
  try {
    const bundle = await apiGet("/home-bundle");
    if (bundle && typeof bundle === "object" && "page" in bundle) return bundle;
  } catch {
    /* sin endpoint agregado: se usa el respaldo */
  }

  const rutas = [
    ["page", "/pages/home"],
    ["word_of_day", "/word-of-day"],
    ["stats", "/stats"],
    ["events", "/events"],
    ["latest_articles", "/articles/latest"],
    ["podcast", "/podcast"],
  ];

  const resultados = await Promise.allSettled(rutas.map(([, ruta]) => apiGet(ruta)));
  const bundle = {};
  resultados.forEach((res, i) => {
    bundle[rutas[i][0]] = res.status === "fulfilled" ? res.value : null;
  });
  return bundle;
}
